import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/api";
import { useAuth } from "@/auth";
import { toast } from "sonner";
import { Calendar, CheckCircle2, XCircle, RefreshCw } from "lucide-react";

export default function Settings() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [sp, setSp] = useSearchParams();

  const load = () =>
    api.get("/oauth/google/status").then((r) => setStatus(r.data)).catch(() => setStatus(null));

  useEffect(() => {
    load();
    const flag = sp.get("google");
    if (flag === "connected") toast.success("Google Calendar connected");
    if (flag === "error") toast.error("Google connect failed. Check backend logs.");
    if (flag === "notconfigured") toast.error("Backend is missing GOOGLE_CLIENT_SECRET.");
    if (flag) { sp.delete("google"); setSp(sp, { replace: true }); }
    // eslint-disable-next-line
  }, []);

  const connect = async () => {
    try {
      const { data } = await api.get("/oauth/google/login");
      window.location.href = data.authorization_url;
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const disconnect = async () => {
    try { await api.post("/oauth/google/disconnect"); toast.success("Disconnected"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const pull = async () => {
    try { const { data } = await api.post("/oauth/google/sync-pull"); toast.success(`Pulled ${data.fetched} events`); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  if (user?.role !== "OWNER") {
    return <div className="p-10 text-sm text-[#52525B]">Only clinic owners can access settings.</div>;
  }

  return (
    <div className="p-8 max-w-[900px] mx-auto">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-widest text-[#E27B58] font-semibold mb-1">Preferences</div>
        <h1 className="font-display text-3xl font-bold tracking-tight">Settings</h1>
      </div>

      {/* Google Calendar card */}
      <div className="bg-white border border-[#E5E5E2] rounded-md p-6">
        <div className="flex items-start justify-between gap-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-md bg-[#F4F4F5] flex items-center justify-center">
              <Calendar className="w-5 h-5 text-[#0F4C3A]" />
            </div>
            <div>
              <h2 className="font-display text-lg font-bold tracking-tight">Google Calendar Sync</h2>
              <p className="text-sm text-[#52525B] mt-1">
                Two-way sync: DentOS appointments are pushed to your Google Calendar automatically, and you can pull external events.
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-3 text-xs">
                <span className="inline-flex items-center gap-1">
                  {status?.configured ? <CheckCircle2 className="w-3.5 h-3.5 text-[#15803D]" /> : <XCircle className="w-3.5 h-3.5 text-[#D93838]" />}
                  Backend configured
                </span>
                <span className="inline-flex items-center gap-1">
                  {status?.connected ? <CheckCircle2 className="w-3.5 h-3.5 text-[#15803D]" /> : <XCircle className="w-3.5 h-3.5 text-[#D93838]" />}
                  {status?.connected ? `Connected as ${status.email}` : "Not connected"}
                </span>
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-2 shrink-0">
            {!status?.connected ? (
              <button data-testid="gcal-connect-btn" onClick={connect} disabled={!status?.configured}
                className="text-sm px-4 py-2 bg-[#0F4C3A] text-white rounded-md hover:bg-[#16654F] disabled:opacity-50">
                Connect Google
              </button>
            ) : (
              <>
                <button onClick={pull} className="text-sm px-4 py-2 border border-[#E5E5E2] rounded-md hover:bg-[#F4F4F5] inline-flex items-center gap-1 justify-center">
                  <RefreshCw className="w-4 h-4" /> Pull events
                </button>
                <button onClick={disconnect} className="text-sm px-4 py-2 border border-[#FECACA] text-[#991B1B] rounded-md hover:bg-[#FEE2E2]">
                  Disconnect
                </button>
              </>
            )}
          </div>
        </div>
        {!status?.configured && (
          <div className="mt-4 text-xs bg-[#FEF3C7] border border-[#FDE68A] rounded-sm p-3 text-[#92400E]">
            Backend missing <code>GOOGLE_CLIENT_SECRET</code>. Add it to <code>/app/backend/.env</code> and restart the backend.
          </div>
        )}
        <div className="mt-4 text-xs text-[#52525B]">
          Redirect URI to whitelist in Google Cloud Console:<br/>
          <code className="font-mono-num text-[#1C1C1C]">{status?.redirect_uri || "(loading)"}</code>
        </div>
      </div>
    </div>
  );
}
